import { useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { X, Link } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { usePanelShown } from '../../utils/panelShown';
import { useLeavingPanel } from '../../hooks/useLeavingPanel';
import { PLAN_PANEL_WIDTH } from '../../theme/layout';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { isEscapeLayerOpen } from '../../utils/escapeStack';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useAuthGate } from '../../hooks/useAuthGate';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { sharePlan } from '../../utils/share';
// Own files, not the `../common` barrel — see DefensePlanPanel.web.tsx's
// comment on why (the barrel pulls in gorhom, which breaks this file's jest
// render test).
import { Button } from '../common/Button';
import { TextLink } from '../common/TextLink';
import { useConfirm } from '../common/ConfirmDialog';
import { CompanionPanel, useCompanionPlacement } from '../layout/CompanionPanel';
import { usePanelHistoryEntry } from '../../hooks/usePanelHistory';
import { DefensePlanFeatureRow } from './DefensePlanFeatureRow.web';
import { useRevealSelectedPlanRow } from './useRevealSelectedPlanRow.web';
import { DefensePlanBundleGroup } from './DefensePlanBundleGroup.web';
import { DefensePlanBundleRow, BUNDLE_COLLAPSE_THRESHOLD } from './DefensePlanBundleRow.web';
import { DefensePlanAssignSheet } from './DefensePlanAssignSheet.web';
import { DefensePlanAssignForm, assignTargetLabel } from './DefensePlanAssignForm.web';
import { DEFENSE_STRATEGIES } from './defenseStrategies';
import {
  summarizeAssignments,
  assignmentSummaryText,
  bundleRowWhoText,
  linksForFeature,
} from '../../utils/planAssignmentSummary';
import { groupFeaturesForList } from '../../utils/planBundles';
import { nextEventForBundle } from '../../utils/eventCalendar';
import { usePlanEventsStore } from '../../store/planEventsStore';
import { computeFeatureLabels } from '../../utils/planFeatureLabels';
import type { DefensePlan, PlanEvent } from '../../types';

const NO_EVENTS: PlanEvent[] = [];

// How long the "That plan isn't available anymore" notice stays up before
// the panel closes itself — long enough to read, short enough that it
// doesn't feel stuck.
const NOTICE_DISMISS_MS = 4000;

/**
 * Read-only content for the plan viewer's DockedPanel (mounted by
 * app/(tabs)/index.tsx as `<DockedPanel width={PLAN_PANEL_WIDTH}
 * testID="plan-viewer-panel">`), shown whenever `uiStore.viewedPlanId`
 * points at a plan and the editor isn't docked. Same shell shape as
 * DefensePlanPanel.web.tsx (also .web-only for the same reason: zustand
 * only, no Leaflet), but with no write path at all — no title edit, no
 * strategy palette, no delete. A row still fits the map to its feature
 * (DefensePlanFeatureRow gives that for free via onSelect); "Show whole
 * plan" undoes that back to the plan's own bounds.
 *
 * `plan` goes null when the plan it was pointed at stops being available
 * mid-view (deleted, or made private by someone else) — the store clears it
 * and sets `notice` to a short explanation
 * (defensePlansStore.subscribeViewedPlan's permission-denied branch). This
 * component then shows the notice by itself and closes a few seconds later,
 * rather than just vanishing.
 */
export function DefensePlanViewer({
  plan,
  notice,
}: {
  plan: DefensePlan | null;
  notice?: string | null;
}) {
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  const selectedPlanFeatureId = useUIStore((s) => s.selectedPlanFeatureId);
  const setSelectedPlanFeature = useUIStore((s) => s.setSelectedPlanFeature);
  const requestPlanFeatureFocus = useUIStore((s) => s.requestPlanFeatureFocus);
  const selectedPlanBundleId = useUIStore((s) => s.selectedPlanBundleId);
  const setSelectedPlanBundle = useUIStore((s) => s.setSelectedPlanBundle);
  const requestPlanBundleFocus = useUIStore((s) => s.requestPlanBundleFocus);
  const requestPlanPanelOpen = useUIStore((s) => s.requestPlanPanelOpen);
  const requestMapPlanFocus = useUIStore((s) => s.requestMapPlanFocus);
  const setViewedPlanId = useUIStore((s) => s.setViewedPlanId);
  const clearViewedPlanNotice = useDefensePlansStore((s) => s.clearViewedPlanNotice);
  const partiesByPlan = useDefensePlansStore((s) => s.partiesByPlan);
  const assignmentsByPlan = useDefensePlansStore((s) => s.assignmentsByPlan);
  // Events scheduled on this plan's bundles, for the next-event line under each
  // bundle (subscribed by useDefensePlanLayer while the plan is viewed).
  const planEvents =
    usePlanEventsStore((s) => (plan ? s.eventsByPlan[plan.id] : undefined)) ?? NO_EVENTS;
  const volunteerForFeature = useDefensePlansStore((s) => s.volunteerForFeature);
  const updateAssignmentStatus = useDefensePlansStore((s) => s.updateAssignmentStatus);
  const removeAssignment = useDefensePlansStore((s) => s.removeAssignment);
  const { requireAuth } = useAuthGate();
  const confirm = useConfirm();
  const isDesktopShell = useDesktopShell();
  const desktopCompanionFolded = useCompanionPlacement(PLAN_PANEL_WIDTH) === 'folded';

  const [justCopied, setJustCopied] = useState(false);
  useEffect(() => {
    if (!justCopied) return;
    const timer = setTimeout(() => setJustCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [justCopied]);

  const [assignFeatureId, setAssignFeatureId] = useState<string | null>(null);
  // Companion stays mounted with its last feature while it slides out.
  const assignLeave = useLeavingPanel(assignFeatureId != null, assignFeatureId);
  const shownAssignId = assignLeave.lastContent ?? null;
  const openAssign = (featureId: string) => {
    setAssignFeatureId(featureId);
    useUIStore.getState().setPanelNewest('left');
  };
  const closeAssign = () => setAssignFeatureId(null);
  // Browser history for the viewer's own Assign entry point
  // (panel-system.md's "Deep links and back", Phase 4A) — same shape as
  // DefensePlanPanel.web.tsx's companion, a separate key since the two can't
  // both be mounted (editor and viewer share the one left dock).
  const closeAssignAndPop = usePanelHistoryEntry(
    isDesktopShell,
    assignFeatureId != null,
    'plan-viewer-assign',
    closeAssign
  );
  const [bundleExpandedOverrides, setBundleExpandedOverrides] = useState<Record<string, boolean>>(
    {}
  );
  const resolveBundleExpanded = (bundleId: string, memberCount: number): boolean =>
    bundleExpandedOverrides[bundleId] ?? memberCount <= BUNDLE_COLLAPSE_THRESHOLD;
  const toggleBundleExpanded = (bundleId: string, memberCount: number) => {
    setBundleExpandedOverrides((prev) => ({
      ...prev,
      [bundleId]: !resolveBundleExpanded(bundleId, memberCount),
    }));
  };
  // Shape click on the map: open the feature's bundle, scroll its row into
  // view.
  useRevealSelectedPlanRow(
    selectedPlanFeatureId,
    plan?.features,
    resolveBundleExpanded,
    (bundleId) => setBundleExpandedOverrides((prev) => ({ ...prev, [bundleId]: true }))
  );
  const handleBundleHeaderSelect = (bundleId: string) => {
    setSelectedPlanBundle(bundleId);
    requestPlanBundleFocus(bundleId);
  };
  const parties = plan ? (partiesByPlan[plan.id] ?? {}) : {};
  const assignments = plan ? (assignmentsByPlan[plan.id] ?? {}) : {};
  // A signed-in viewer's own party on this plan, if they have one — a
  // homeowner's party is reused across defenses, so this is at most one.
  const myPartyId = user
    ? (Object.values(parties).find((p) => p.uid === user.id)?.id ?? null)
    : null;

  const handleClose = () => {
    setViewedPlanId(null);
    clearViewedPlanNotice();
  };

  // Auto-dismiss the notice once it's had time to be read. Keyed on
  // `notice` alone — handleClose only ever calls setViewedPlanId and
  // clearViewedPlanNotice, both stable zustand actions, so it doesn't need
  // to be a dependency itself.
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(handleClose, NOTICE_DISMISS_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice]);

  // False while this panel slides out: the Escape listener below detaches.
  const panelShown = usePanelShown();

  // Escape closes the viewer, same guard the editor's Escape handler uses:
  // not while focus is in a text field. Nothing in this read-only panel is
  // one today, but the guard keeps the two panels' behaviour identical if
  // that ever changes. Runs before the `!plan` early return below — every
  // hook in this component has to run on every render regardless of which
  // branch it takes (rules-of-hooks), so this can't move after it.
  useEffect(() => {
    // Guards a jest render (react-native test environment, no DOM) rather
    // than a real runtime condition — this component only ever mounts on
    // web, where `document` always exists.
    if (typeof document === 'undefined' || !panelShown) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A dialog over the panel owns this press (utils/escapeStack).
      if (isEscapeLayerOpen()) return;
      const el = document.activeElement as HTMLElement | null;
      const isTyping =
        !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (isTyping) return;
      handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // handleClose is recreated every render but does the same two calls
    // each time; keying on its real dependencies (setViewedPlanId /
    // clearViewedPlanNotice) avoids re-binding the listener on every
    // render for no behavior change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setViewedPlanId, clearViewedPlanNotice, panelShown]);

  if (!plan) {
    return (
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: colors.text }]}>Defense plan</Text>
          <Pressable
            onPress={handleClose}
            testID="plan-viewer-close"
            accessibilityRole="button"
            accessibilityLabel="Close defense plan"
            style={[styles.closeButton, CURSOR_POINTER]}>
            <X size={18} color={colors.textSecondary} />
          </Pressable>
        </View>
        {notice && (
          <Text
            style={[styles.metaLine, { color: colors.textSecondary }]}
            testID="plan-viewer-notice">
            {notice}
          </Text>
        )}
      </View>
    );
  }

  const handleFeatureSelect = (featureId: string) => {
    setSelectedPlanFeature(featureId);
    requestPlanFeatureFocus(featureId);
  };

  // Re-fits to the whole plan's bounds via the same channel a share link or
  // a "Plans in this area" card uses (useMapFocus.focusPlan) rather than
  // reaching for the map handle directly — that channel already knows how
  // to pad around the docked panel, so there's nothing to duplicate here.
  const handleShowWholePlan = () => {
    setSelectedPlanFeature(null);
    requestMapPlanFocus(plan.id);
  };

  const handleShare = async () => {
    const result = await sharePlan({ planId: plan.id, title: plan.title });
    if (result === 'copied') {
      useUIStore.getState().showToast({ message: 'Link copied' });
      setJustCopied(true);
    }
    if (result === 'failed') {
      useUIStore.getState().showToast({ message: "Couldn't copy the link" });
    }
  };

  const isOwner = !!user && user.id === plan.ownerId;
  const summary = summarizeAssignments(plan.features, assignments);
  // Tells same-strategy features apart in the list ("Fire break 1", "Fire
  // break 2", ...) — see utils/planFeatureLabels.
  const featureLabels = computeFeatureLabels(plan.features, (s) => DEFENSE_STRATEGIES[s].label);

  const handleVolunteer = (featureId: string) => {
    requireAuth(() => void volunteerForFeature(plan.id, featureId));
  };

  const handleMarkDone = (assignmentId: string) => {
    void updateAssignmentStatus(plan.id, assignmentId, 'done');
  };

  const handleStepBack = async (assignmentId: string) => {
    const ok = await confirm({
      title: 'Step back from this defense?',
      message: "You'll no longer be listed as responsible for it.",
      confirmLabel: 'Step back',
      destructive: true,
    });
    if (!ok) return;
    void removeAssignment(plan.id, assignmentId);
  };

  return (
    <View style={styles.content}>
      <View style={styles.header}>
        <Text
          style={[styles.title, { color: colors.text }]}
          numberOfLines={2}
          testID="plan-viewer-title">
          {plan.title}
        </Text>
        <Pressable
          onPress={handleClose}
          testID="plan-viewer-close"
          accessibilityRole="button"
          accessibilityLabel="Close defense plan"
          style={[styles.closeButton, CURSOR_POINTER]}>
          <X size={18} color={colors.textSecondary} />
        </Pressable>
      </View>
      <Text style={[styles.metaLine, { color: colors.textSecondary }]}>
        {plan.ownerName} · {assignmentSummaryText(summary)}
      </Text>

      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>In this plan</Text>
        {summary.total > 0 && (
          <TextLink
            label="Show whole plan"
            onPress={handleShowWholePlan}
            textStyle={TYPOGRAPHY.caption}
            testID="plan-viewer-show-whole"
          />
        )}
      </View>
      {summary.total > 0 && (
        <Text style={[styles.rowsHint, { color: colors.textTertiary }]}>
          Select a defense to zoom to it.
        </Text>
      )}
      {summary.total > 0 ? (
        <View style={styles.featureList}>
          {groupFeaturesForList(plan.features, plan.bundles ?? []).map((entry) => {
            if (entry.type === 'feature') {
              const feature = entry.feature;
              const links = linksForFeature(feature.id, assignments);
              const myLink = myPartyId
                ? (links.find((l) => l.partyId === myPartyId) ?? null)
                : null;
              return (
                <DefensePlanFeatureRow
                  key={feature.id}
                  feature={feature}
                  selected={selectedPlanFeatureId === feature.id}
                  active={isDesktopShell && assignFeatureId === feature.id}
                  onSelect={() => handleFeatureSelect(feature.id)}
                  displayLabel={featureLabels[feature.id]}
                  who={{
                    links,
                    parties,
                    onAssign: isOwner ? () => openAssign(feature.id) : undefined,
                    onVolunteer:
                      !isOwner && !myLink ? () => handleVolunteer(feature.id) : undefined,
                    onMarkDone:
                      !isOwner && myLink && myLink.status === 'accepted'
                        ? () => handleMarkDone(myLink.id)
                        : undefined,
                    onStepBack:
                      !isOwner && myLink ? () => void handleStepBack(myLink.id) : undefined,
                  }}
                />
              );
            }
            const { bundle, members } = entry;
            const expanded = resolveBundleExpanded(bundle.id, members.length);
            const bundleSelected = selectedPlanBundleId === bundle.id;
            return (
              <DefensePlanBundleGroup
                key={bundle.id}
                selected={bundleSelected}
                expanded={expanded}
                header={
                  <DefensePlanBundleRow
                    bundle={bundle}
                    members={members}
                    selected={bundleSelected}
                    expanded={expanded}
                    onToggleExpanded={() => toggleBundleExpanded(bundle.id, members.length)}
                    onSelectHeader={() => handleBundleHeaderSelect(bundle.id)}
                    who={{
                      text: bundleRowWhoText(bundle, members, assignments, parties),
                    }}
                    nextEvent={nextEventForBundle(planEvents, plan?.id ?? '', bundle.id)}
                    // Shared plans only: the opportunity page refuses a private one.
                    planId={plan?.visibility === 'shared' ? plan.id : undefined}
                  />
                }>
                {members.map((feature) => {
                  const links = linksForFeature(feature.id, assignments);
                  const myLink = myPartyId
                    ? (links.find((l) => l.partyId === myPartyId) ?? null)
                    : null;
                  return (
                    <DefensePlanFeatureRow
                      nested
                      key={feature.id}
                      feature={feature}
                      selected={selectedPlanFeatureId === feature.id}
                      active={isDesktopShell && assignFeatureId === feature.id}
                      onSelect={() => handleFeatureSelect(feature.id)}
                      displayLabel={featureLabels[feature.id]}
                      who={{
                        links,
                        parties,
                        onAssign: isOwner ? () => openAssign(feature.id) : undefined,
                        onVolunteer:
                          !isOwner && !myLink ? () => handleVolunteer(feature.id) : undefined,
                        onMarkDone:
                          !isOwner && myLink && myLink.status === 'accepted'
                            ? () => handleMarkDone(myLink.id)
                            : undefined,
                        onStepBack:
                          !isOwner && myLink ? () => void handleStepBack(myLink.id) : undefined,
                      }}
                    />
                  );
                })}
              </DefensePlanBundleGroup>
            );
          })}
        </View>
      ) : (
        <Text style={[styles.emptyState, { color: colors.textTertiary }]}>Nothing drawn yet.</Text>
      )}

      {/* Every panel footer is sm, 38px tall — this one was the default md,
          46px (Sept 29 2026, M7). */}
      <View style={[styles.footer, { borderTopColor: colors.border }]}>
        {isOwner && (
          <Button
            title="Edit plan"
            onPress={() => requestPlanPanelOpen()}
            variant="secondary"
            size="sm"
            testID="plan-viewer-edit"
            accessibilityLabel="Edit this plan"
            fullWidth
          />
        )}
        <Button
          title={justCopied ? 'Link copied' : 'Copy link'}
          onPress={() => void handleShare()}
          variant="primary"
          size="sm"
          leftIcon={Link}
          testID="plan-viewer-share"
          accessibilityLabel="Copy plan link"
          fullWidth
        />
      </View>

      {isOwner && isDesktopShell && assignLeave.mounted && shownAssignId != null && (
        <CompanionPanel
          shown={assignLeave.shown}
          onExited={assignLeave.onExited}
          attachTo="plan-viewer-panel"
          parentWidth={PLAN_PANEL_WIDTH}
          folded={desktopCompanionFolded}
          overline="Assign"
          title={assignTargetLabel(
            plan.features.find((f) => f.id === shownAssignId) ?? null,
            null,
            featureLabels[shownAssignId]
          )}
          subtitle="Who's responsible?"
          onClose={closeAssignAndPop}>
          <DefensePlanAssignForm
            planId={plan.id}
            feature={plan.features.find((f) => f.id === shownAssignId) ?? null}
            featureLabel={featureLabels[shownAssignId]}
            objectInHeader
            parties={parties}
            links={linksForFeature(shownAssignId, assignments)}
            areaId={plan.areaId}
            onClose={closeAssignAndPop}
            historyEnabled
          />
        </CompanionPanel>
      )}
      {isOwner && !isDesktopShell && (
        <DefensePlanAssignSheet
          visible={assignFeatureId != null}
          planId={plan.id}
          feature={plan.features.find((f) => f.id === assignFeatureId) ?? null}
          parties={parties}
          links={assignFeatureId ? linksForFeature(assignFeatureId, assignments) : []}
          areaId={plan.areaId}
          onClose={() => setAssignFeatureId(null)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
    flex: 1,
  },
  closeButton: {
    padding: SPACING.xxs,
  },
  metaLine: {
    ...TYPOGRAPHY.metadata,
    marginTop: SPACING.xs,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: SPACING.md,
    marginBottom: SPACING.xs,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
  },
  rowsHint: {
    ...TYPOGRAPHY.caption,
    marginTop: -SPACING.xxs,
    marginBottom: SPACING.xs,
  },
  featureList: {
    gap: SPACING.xs,
  },
  emptyState: {
    ...TYPOGRAPHY.caption,
  },
  footer: {
    marginTop: 'auto',
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.sm,
  },
});
